import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../../context/authStore';
import { authAPI } from '../../services/api';

import ServerWakeNotice from '../../components/auth/ServerWakeNotice';
const toBuffer = (value) => {
  const normalized = String(value || '').replace(/-/g, '+').replace(/_/g, '/');
  const pad = normalized.length % 4 ? '='.repeat(4 - (normalized.length % 4)) : '';
  const raw = window.atob(normalized + pad);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const toBase64Url = (buffer) => {
  const bytes = buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : new Uint8Array(buffer || []);
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [twoFactorMethod, setTwoFactorMethod] = useState('email');
  const [challengeEmailMasked, setChallengeEmailMasked] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [devCodeHint, setDevCodeHint] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const { login, loginWithPasskey, completeTwoFactorLogin, isLoading, error, clearTwoFactorPending } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => () => clearTwoFactorPending(), [clearTwoFactorPending]);

  const startCooldown = (seconds = 45) => {
    const initial = Math.max(0, Number(seconds) || 0);
    setResendCooldown(initial);
    if (initial <= 0) return;
    const id = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const result = await login(email, password);
      if (result?.requiresTwoFactor) {
        setLoginEmail(result.email || email);
        setChallengeId(result.challengeId || '');
        setTwoFactorMethod(result.method || 'email');
        setChallengeEmailMasked(result.maskedEmail || '');
        setDevCodeHint(result.devCode || '');
        setTwoFactorCode('');
        setUseBackupCode(false);
        startCooldown((result.method || 'email') === 'email' ? (result.resendAvailableIn || 45) : 0);
        toast.success((result.method || 'email') === 'totp' ? 'Enter authenticator code to continue' : 'Verification code sent to your email');
        return;
      }
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'Invalid credentials');
    }
  };

  const handleVerifyTwoFactor = async (e) => {
    e.preventDefault();
    if (!twoFactorCode || !challengeId || !loginEmail) {
      toast.error('Enter the verification code');
      return;
    }
    try {
      await completeTwoFactorLogin(loginEmail, challengeId, twoFactorCode, rememberDevice);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'Invalid verification code');
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    try {
      const { data } = await authAPI.login({
        email: loginEmail || email,
        password,
        challengeId,
        resend2fa: true,
      });
      if (data?.requiresTwoFactor) {
        setChallengeId(data.challengeId || challengeId);
        setChallengeEmailMasked(data.maskedEmail || challengeEmailMasked);
        startCooldown(data.resendAvailableIn || 45);
        if ((data.message || '').toLowerCase().includes('wait')) {
          toast((data.message || 'Please wait before requesting another code.'));
        } else {
          toast.success(data.message || 'New verification code sent');
        }
      } else if (data?.token) {
        toast.success('Signed in');
        navigate('/dashboard');
      } else {
        toast.error('Unable to resend verification code');
      }
    } catch (err) {
      toast.error(err.message || 'Unable to resend verification code');
    }
  };

  const handlePasskeySignIn = async () => {
    const normalizedEmail = String(email || '').toLowerCase().trim();
    if (!normalizedEmail) {
      toast.error('Enter your email first');
      return;
    }
    if (!window.PublicKeyCredential || !navigator.credentials?.get) {
      toast.error('Passkeys are not supported on this device/browser');
      return;
    }

    try {
      const { data } = await authAPI.passkeyLoginOptions({ email: normalizedEmail });
      const options = data?.options;
      if (!options?.challenge) {
        toast.error('Passkey challenge unavailable');
        return;
      }
      const publicKey = {
        ...options,
        challenge: toBuffer(options.challenge),
        allowCredentials: (options.allowCredentials || []).map((item) => ({
          ...item,
          id: toBuffer(item.id),
        })),
      };
      const assertion = await navigator.credentials.get({ publicKey });
      if (!assertion) throw new Error('Passkey prompt was cancelled');
      const response = assertion.response;
      const credential = {
        id: assertion.id,
        rawId: toBase64Url(assertion.rawId),
        type: assertion.type,
        authenticatorAttachment: assertion.authenticatorAttachment || null,
        response: {
          clientDataJSON: toBase64Url(response.clientDataJSON),
          authenticatorData: toBase64Url(response.authenticatorData),
          signature: toBase64Url(response.signature),
          userHandle: response.userHandle ? toBase64Url(response.userHandle) : null,
        },
        clientExtensionResults: assertion.getClientExtensionResults?.() || {},
      };
      await loginWithPasskey(normalizedEmail, credential);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.message || 'Passkey sign-in failed');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - form */}
      <div className="relative flex-1 flex items-center justify-center p-8">
        <Link
          to="/"
          className="absolute left-6 top-6 inline-flex items-center gap-2 text-sm font-medium text-secondary-600 hover:text-secondary-900"
        >
          <ArrowLeft size={18} />
          Back to homepage
        </Link>
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center gap-4 mb-8">
            <Link to="/" className="block">
              <img src="/branding/stratel-logo-dark.png" alt="Stratel" className="object-contain h-36 md:h-44 lg:h-56" onError={(e)=>{e.currentTarget.src='/branding/stratel-logo-placeholder.svg'}} />
            </Link>

            <h1 className="text-3xl md:text-4xl font-display font-bold text-secondary-900 mt-4">
              Welcome back
            </h1>
          </div>
          <p className="mt-2 text-secondary-600 text-center">
            {challengeId ? 'Enter your verification code to finish sign-in.' : 'Sign in to access your projects and documents.'}
          </p>

          {!challengeId ? (
          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div>
              <label htmlFor="email" className="label">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input"
                placeholder="you@company.com"
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="input pr-10"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-secondary-600">Remember me</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Forgot password?
              </Link>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
            <ServerWakeNotice active={isLoading} />
            <button
              type="button"
              disabled={isLoading}
              onClick={handlePasskeySignIn}
              className="btn-secondary w-full"
            >
              Sign in with passkey
            </button>
          </form>
          ) : (
          <form onSubmit={handleVerifyTwoFactor} className="mt-8 space-y-6">
            <div>
              <label htmlFor="verification-code" className="label">
                Verification Code
              </label>
              <input
                type="text"
                id="verification-code"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                required
                className="input"
                placeholder={useBackupCode ? 'Enter backup code' : (twoFactorMethod === 'totp' ? 'Enter 6-digit authenticator code' : 'Enter 6-digit code')}
                autoComplete="one-time-code"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={useBackupCode}
                onChange={(e) => setUseBackupCode(e.target.checked)}
                className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-secondary-600">Use backup code</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-secondary-600">Remember this device for 30 days</span>
            </label>
            {challengeEmailMasked && (
              <div className="text-xs text-secondary-500">
                Code sent to {challengeEmailMasked}
              </div>
            )}
            {devCodeHint && import.meta.env.DEV && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                Dev OTP: {devCodeHint}
              </div>
            )}
            <div className="flex items-center justify-between">
              {twoFactorMethod === 'email' ? (
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0 || isLoading}
                  className="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              ) : <span className="text-xs text-secondary-500">Authenticator app enabled</span>}
              <button
                type="button"
                onClick={() => {
                  setChallengeId('');
                  setTwoFactorMethod('email');
                  setTwoFactorCode('');
                  setChallengeEmailMasked('');
                  setDevCodeHint('');
                  clearTwoFactorPending();
                }}
                className="text-sm text-secondary-600 hover:text-secondary-700"
              >
                Back to login
              </button>
            </div>
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? 'Verifying...' : 'Verify & Sign in'}
            </button>
          </form>
          )}

          <p className="mt-8 text-center text-secondary-600">
            Don't have an account?{' '}
            <Link to="/contact" className="text-primary-600 hover:text-primary-700 font-medium">
              Contact us
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - branding */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary-600 to-primary-800 items-center justify-center p-12">
        <div className="max-w-md text-white">
          <h2 className="text-3xl font-display font-bold">
            Manage Your Projects with Ease
          </h2>
          <p className="mt-4 text-primary-100 text-lg">
            Access your documents, track project progress, communicate with our team, 
            and manage invoices all in one place.
          </p>
          <div className="mt-8 space-y-4">
            {[
              'Real-time document collaboration',
              'Secure file sharing and storage',
              'Transparent project tracking',
              'Easy invoice management',
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
